/**
 * Acceso.tsx — entrar, y elegir en qué libros se trabaja.
 *
 * Es `login.html` de la Fase 0, con los estados que el mockup no tenía porque
 * un mockup siempre está configurado, siempre autentica y siempre tiene
 * empresas. Los que faltaban son justo los que se ven en la vida real:
 *
 *   · sin configurar     · comprobando la sesión
 *   · autenticado y sin ninguna empresa
 *   · no se pudieron listar las empresas
 *
 * ── Dos cosas del mockup que no se construyeron, y por qué ────────────────
 *
 * **«Cree su empresa gratis».** Prometía un registro que el sistema niega:
 * `control.abrir_sesion` se niega a crear a nadie sin invitación previa, y a
 * propósito —un alta automática deja cuentas sin empresa y, peor, un camino
 * para probar correos—. El pie ahora dice cómo se entra de verdad.
 *
 * **«Iniciar sesión con Microsoft».** No es un botón: es un retorno de OAuth,
 * y ese retorno trae la credencial en la URL. Acá se apagó
 * `detectSessionInUrl` justamente para que el token no quede en el historial
 * del navegador ni en el `Referer` de la primera petición. Reconciliar las dos
 * cosas es una decisión propia, no un añadido de esta pantalla.
 */
import { useState, type FormEvent } from 'react';
import { Texto, useIdioma } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import { supabase } from '../nucleo/supabase.ts';
import { Isotipo } from '../componentes/Iconos.tsx';
import type { Clave } from '../nucleo/idioma.ts';
import { rotuloDeRol } from '../nucleo/roles.ts';

const FALLOS: Readonly<Record<string, Clave>> = {
  credenciales: 'acc.errCredentials',
  sin_conexion: 'acc.errOffline',
  sin_acceso: 'acc.errNoAccess',
  sin_configurar: 'acc.notConfigured',
};

/** El marco oscuro con la consola. Lo comparten los cuatro estados. */
function Marco({ children }: { children: React.ReactNode }) {
  const { t, idioma, cambiar } = useIdioma();
  return (
    <div className="acceso">
      <div className="bgwrap" aria-hidden="true" />
      <div className="shell">
        <div className="win">
          <div className="console">
            <div className="console__bar">
              <span className="console__dots" aria-hidden="true"><i /><i /><i /></span>
              <span className="console__path">skoped-books://</span>
            </div>
            <div className="console__body">{children}</div>
          </div>

          {/* El idioma se elige ANTES de tener sesión: si no, alguien que no
              lee español se queda mirando una pantalla que no entiende. */}
          <div className="langpick" role="group" aria-label="Idioma / Language">
            <button type="button" aria-pressed={idioma === 'es'} onClick={() => cambiar('es')}>
              ESPAÑOL
            </button>
            <button type="button" aria-pressed={idioma === 'en'} onClick={() => cambiar('en')}>
              ENGLISH
            </button>
          </div>

          <div className="brandline">
            <span className="dot">◆</span> {t('login.byLabs')}
          </div>
        </div>
      </div>
    </div>
  );
}

function Encabezado({ sub }: { sub?: string }) {
  const { t } = useIdioma();
  return (
    <>
      <Isotipo className="iso-glow" tono="oscuro" />
      {/* ▶ `h1` y no `div`. En una pantalla de acceso el título de la página
          ES el nombre del producto: no hay otro. Era un `div`, así que esta
          pantalla no tenía encabezado de nivel 1 — y el `margin:0` mantiene
          exactamente el aspecto que tenía, porque esto no cambia cómo se ve. */}
      <h1 className="title wordmark wordmark--dark" style={{ fontSize: 21, margin: 0 }}>
        Skoped <span>Books</span>
      </h1>
      <p className="sub">{sub ?? t('login.sub')}</p>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

export function Acceso() {
  const { t } = useIdioma();
  const { pantalla, empresas, esOperador, fallo, ocupado, entrar, elegir, salir } = useSesion();
  const [correo, setCorreo] = useState('');
  const [clave, setClave] = useState('');
  /**
   * ▶ «Olvidé mi contraseña» ya existía EN EL DICCIONARIO, en los dos
   * idiomas, y ninguna pantalla lo enseñaba.
   *
   * Alguien previó el enlace, escribió las dos frases y nunca cablé la
   * pantalla. Es la misma familia que `es_operador` sin escritor o
   * `control.bitacora` sin llamador —una promesa escrita y no cumplida—,
   * pero en el diccionario, que es donde ningún guardián mira: el 10 vigila
   * endpoints sin pantalla y el 9 columnas sin uso, y **una clave de texto sin
   * lector no la vigila nadie**.
   *
   * Y no era cosmético: sin esto, el primer cliente que olvide su contraseña
   * tiene que escribirnos — con E8 tratando justo de que el arranque no
   * dependa de nosotros.
   */
  const [olvido, setOlvido] = useState(false);
  const [pedido, setPedido] = useState(false);
  const [pidiendo, setPidiendo] = useState(false);

  if (pantalla === 'sin_configurar') {
    return (
      <Marco>
        <Encabezado sub={t('acc.notConfigured')} />
        <div className="alert alert--warn"><span>{t('acc.notConfiguredBody')}</span></div>
      </Marco>
    );
  }

  if (pantalla === 'cargando') {
    return (
      <Marco>
        <Encabezado sub={t('acc.checking')} />
        <div className="bar"><div className="bar__fill" style={{ width: '40%' }} /></div>
      </Marco>
    );
  }

  if (pantalla === 'sin_empresas') {
    // Un fallo de conexión y no pertenecer a ninguna empresa se cuentan
    // DISTINTO. Confundirlos manda a alguien a pedir una invitación que ya
    // tiene, o a esperar por una que nunca llegó.
    const fueFallo = fallo === 'sin_conexion' || fallo === 'no_se_pudo_listar';

    /**
     * ▶ Y UN OPERADOR SIN EMPRESAS NO ESTÁ PERDIDO: ES SU ESTADO NORMAL.
     *
     * El modelo de la suite (§4.1) dice que el admin global entra a la consola
     * de administración y **no como usuario cliente de ninguna instancia**. O
     * sea que su cuenta tiene cero membresías a propósito, para siempre.
     *
     * Hasta hoy esta pantalla le decía «su usuario no está en ninguna empresa,
     * su administrador puede agregarlo» — a la persona que ES el
     * administrador— y **no ofrecía la consola**: el enlace vivía solo en la
     * rama de elegir empresa. La ruta funcionaba tecleándola; la pantalla no
     * llevaba a ella.
     *
     * Es exactamente la deuda que teníamos escrita y sin resolver: *ningún
     * control comprueba que una pantalla sea alcanzable*. Se destapó al aplicar
     * el §4.1 y dejar esa cuenta como queda de verdad.
     */
    const esOperadorSinEmpresas = esOperador && !fueFallo;

    return (
      <Marco>
        <Encabezado sub={t(esOperadorSinEmpresas ? 'acc.operatorOnly'
          : fueFallo ? 'acc.listFailed' : 'acc.noCompanies')} />
        <div className={`alert ${fueFallo ? 'alert--warn' : 'alert--info'}`}>
          <span>
            {t(esOperadorSinEmpresas ? 'acc.operatorOnlyBody'
              : fueFallo ? 'acc.listFailedBody' : 'acc.noCompaniesBody')}
          </span>
        </div>

        {esOperador && (
          <p className="foot" style={{ marginTop: 'var(--s6)' }}>
            <a className="btn btn--secondary btn--sm" href="/consola">
              {t('op.console')}
            </a>
          </p>
        )}

        <div className="row" style={{ marginTop: 'var(--s7)', gap: 'var(--s4)' }}>
          {fueFallo && (
            <button className="btn btn--onDark" type="button" onClick={() => location.reload()}>
              {t('acc.retry')}
            </button>
          )}
          <button className="btn btn--onDark" type="button" onClick={() => void salir()}>
            {t('acc.signOut')}
          </button>
        </div>
      </Marco>
    );
  }

  if (pantalla === 'elegir_empresa') {
    return (
      <Marco>
        <div className="label" style={{ color: 'var(--acc-bright)', textAlign: 'center', marginBottom: 'var(--s6)' }}>
          <span>{t('login.signedIn')}</span>
        </div>
        <div className="title" style={{ fontSize: 18 }}>{t('login.whichCompany')}</div>
        <p className="sub">{t('login.rolesSub', { n: String(empresas.length) })}</p>

        <div className="stack" style={{ gap: 'var(--s4)' }}>
          {empresas.map((e) => (
            <button
              key={e.id} className="company-switch" type="button"
              onClick={() => elegir(e.id)}
            >
              <span className="company-switch__mark">
                {e.razonSocial.slice(0, 2).toUpperCase()}
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="company-switch__name">{e.razonSocial}</span><br />
                <span className="company-switch__meta">
                  {t(rotuloDeRol(e.rol))}
                  {/* Una empresa suspendida SE PUEDE elegir. Sacarla de la
                      lista dejaría a alguien sin poder exportar sus periodos
                      cerrados, que es justo lo que la suspensión no puede
                      hacer — CLAUDE.md §7. */}
                  {e.estado !== 'activa' && ` · ${t('acc.floor')}`}
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* ▶ La consola va ENTRE las empresas y el salir, y no dentro de la
            lista: no es una empresa. Ofrecerla como una más haría pensar que
            entrar ahí es entrar a unos libros, y es justo lo contrario. */}
        {esOperador && (
          <p className="foot" style={{ marginTop: 'var(--s6)' }}>
            <a className="btn btn--secondary btn--sm" href="/consola">
              {t('op.console')}
            </a>
          </p>
        )}

        <p className="foot" style={{ marginTop: 'var(--s6)' }}>
          <button
            className="btn btn--ghost btn--sm" type="button"
            onClick={() => void salir()}
            style={{ color: 'var(--acc-bright)' }}
          >
            {t('login.switchUser')}
          </button>
        </p>
      </Marco>
    );
  }

  /* ── Entrar ────────────────────────────────────────────────────────────── */

  const enviar = (ev: FormEvent) => {
    ev.preventDefault();
    void entrar(correo, clave);
  };

  /**
   * Pedir el correo de recuperación.
   *
   * ▶ LA RESPUESTA ES LA MISMA EXISTA O NO LA CUENTA.
   *
   * No se mira el `error` para decidir qué enseñar: decir «esa dirección no
   * está registrada» convertiría esta pantalla en una forma de averiguar
   * quién es cliente nuestro, escribiendo direcciones y mirando cuál cambia el
   * mensaje. Es la misma razón por la que no hay alta automática.
   */
  const pedirCorreo = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!supabase || correo.trim() === '') return;
    setPidiendo(true);
    try {
      await supabase.auth.resetPasswordForEmail(correo.trim(), {
        redirectTo: `${window.location.origin}/clave`,
      });
    } catch {
      // Ni siquiera un fallo de red cambia lo que se enseña: lo único que se
      // afirma es «si esa dirección está en Books, va a llegar un correo», y
      // eso sigue siendo cierto cuando el intento no salió.
    } finally {
      setPidiendo(false);
      setPedido(true);
    }
  };

  if (olvido) {
    return (
      <Marco>
        {/* ▶ «Volver» va ARRIBA, y no debajo del formulario.
         *
         * Abajo caería en el MISMO pixel donde acaba de estar «Olvidé mi
         * contraseña», así que un clic que rebota —o un doble toque en un
         * móvil— abre esta pantalla y la cierra en el mismo gesto, y desde
         * fuera parece que el enlace no hace nada. Apareció revisando la
         * pantalla en el navegador: el clic automatizado la abría y la cerraba
         * sin que se viera nada en medio.
         *
         * Dos controles que se turnan en la misma coordenada son una trampa
         * aunque cada uno funcione. */}
        <p style={{ marginBottom: 'var(--s5)' }}>
          <button
            className="btn btn--ghost btn--sm" type="button"
            style={{ color: 'var(--acc-bright)' }}
            onClick={() => { setOlvido(false); setPedido(false); }}
          >
            ← {t('login.backToSignIn')}
          </button>
        </p>
        <Encabezado sub={t('login.forgotSub')} />
        {pedido ? (
          <>
            {/* Ni «enviado» ni «no existe»: lo que se sabe con certeza. */}
            <div className="alert alert--info"><span>{t('login.forgotSent')}</span></div>
          </>
        ) : (
          <form onSubmit={pedirCorreo} noValidate>
            <label className="f-dark">
              <span>{t('login.email')}</span>
              <input
                type="email" autoComplete="username" required
                placeholder={t('login.emailHint')}
                value={correo} onChange={(e) => setCorreo(e.target.value)}
              />
            </label>
            <button
              className="btn btn--primary btn--block" type="submit"
              style={{ minHeight: 44 }} disabled={pidiendo || correo.trim() === ''}
            >
              {t('login.forgotSend')}
            </button>
          </form>
        )}
      </Marco>
    );
  }

  return (
    <Marco>
      <Encabezado />
      <form onSubmit={enviar} noValidate>
        <label className="f-dark">
          <span>{t('login.email')}</span>
          <input
            type="email" autoComplete="username" required
            placeholder={t('login.emailHint')}
            value={correo} onChange={(e) => setCorreo(e.target.value)}
          />
        </label>
        <label className="f-dark">
          <span>{t('login.password')}</span>
          <input
            type="password" autoComplete="current-password" required
            placeholder="••••••••••"
            value={clave} onChange={(e) => setClave(e.target.value)}
          />
        </label>

        {fallo && FALLOS[fallo] && (
          <p className="err" role="alert" style={{ color: 'var(--danger-border)' }}>
            {t(FALLOS[fallo]!)}
          </p>
        )}

        <button
          className="btn btn--primary btn--block" type="submit"
          style={{ minHeight: 44 }} disabled={ocupado}
        >
          {t('login.enter')}
        </button>

        <p className="foot">
          <button
            className="btn btn--ghost btn--sm" type="button"
            style={{ color: 'var(--acc-bright)' }}
            onClick={() => { setOlvido(true); setPedido(false); }}
          >
            {t('login.forgot')}
          </button>
        </p>
      </form>

      {/* Sin «cree su empresa gratis»: el sistema se niega a crear a nadie sin
          invitación previa, y prometerlo acá sería prometer lo que la base
          rechaza. */}
      <p className="foot" style={{ marginTop: 'var(--s7)' }}>
        <Texto k="acc.byInvitation" />
      </p>
    </Marco>
  );
}
